// src/pages/DevStreamPopout.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useDevStream } from "../hooks/useDevStream";
import { DevStreamPane } from "../components/DevStream";
import SessionExpirationWarning from "../components/SessionExpirationWarning";
import config from "../config";
import logger from "../utils/logger";
import "../components/DevStream/DevStream.css";

/**
 * DevStreamPopout - Standalone page for popped-out dev stream
 * Runs in a separate window with its own WebSocket connection
 */
function DevStreamPopout() {
  const { user, getAccessToken } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessChecking, setAccessChecking] = useState(true);
  const [orgSettings, setOrgSettings] = useState(null); // eslint-disable-line no-unused-vars
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "dark-mode";
  });

  // Get current organization ID from localStorage (DevStream-specific key)
  // Fall back to main app's selected organization if DevStream-specific key is not set
  const selectedOrgId = localStorage.getItem("devStream_selectedOrgId") || 
                       localStorage.getItem("selectedOrgId");

  // Check if user is Full Admin
  const isFullAdmin = user?.roles?.some(
    (role) => (typeof role === "string" ? role : role?.name) === "Full Admin"
  );

  // Initialize DevStream with WebSocket connection (only if has access)
  const devStreamData = useDevStream(user?.userId, hasAccess);

  // Check access on mount and periodically
  useEffect(() => {
    const checkAccess = async () => {
      if (!selectedOrgId) {
        logger.warn(
          "[DevStreamPopout] No organization selected, closing window"
        );
        setHasAccess(false);
        setAccessChecking(false);
        setTimeout(() => window.close(), 2000);
        return;
      }

      // Check if user is Full Admin (always has access)
      if (isFullAdmin) {
        setHasAccess(true);
        setAccessChecking(false);
        return;
      }

      // Fetch org settings to check enableDevStreamsForAllRoles
      try {
        const cacheKey = `orgSettings_${selectedOrgId}`;
        const cached = localStorage.getItem(cacheKey);

        let settings = null;

        // Try cache first
        if (cached) {
          try {
            const { settings: cachedSettings, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

            if (age < CACHE_TTL) {
              settings = cachedSettings;
            }
          } catch (e) {
            logger.warn("[DevStreamPopout] Failed to parse cached settings", e);
          }
        }

        // Fetch from API if not cached
        if (!settings) {
          const token = await getAccessToken();
          const response = await fetch(
            `${config.apiEndpoint}/organizations/${encodeURIComponent(
              selectedOrgId
            )}/settings`,
            {
              method: "GET",
              headers: {
                "x-api-key": config.apiKey,
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            settings = data.settings;
          }
        }

        setOrgSettings(settings);

        // Check access
        const canAccess = settings?.enableDevStreamsForAllRoles === true;
        setHasAccess(canAccess);
        setAccessChecking(false);

        if (!canAccess) {
          logger.warn("[DevStreamPopout] Access denied, closing window");
          setTimeout(() => window.close(), 2000);
        }
      } catch (error) {
        logger.error("[DevStreamPopout] Error checking access:", error);
        setHasAccess(false);
        setAccessChecking(false);
        setTimeout(() => window.close(), 2000);
      }
    };

    checkAccess();

    // Periodic access validation every 60 seconds
    const interval = setInterval(checkAccess, 60000);

    return () => clearInterval(interval);
  }, [selectedOrgId, isFullAdmin, getAccessToken]);

  // Listen for settings changes from main window
  useEffect(() => {
    if (!selectedOrgId) return;

    const cacheKey = `orgSettings_${selectedOrgId}`;

    const handleStorageChange = (e) => {
      // Handle theme changes
      if (e.key === "theme") {
        const newTheme = e.newValue || "dark-mode";
        setTheme(newTheme);
        document.body.className = newTheme;
        return;
      }

      // Handle settings changes
      if (e.key === cacheKey && e.newValue) {
        try {
          const { settings } = JSON.parse(e.newValue);
          setOrgSettings(settings);

          // Check if access was revoked
          const canAccess =
            isFullAdmin || settings?.enableDevStreamsForAllRoles === true;

          if (!canAccess && hasAccess) {
            logger.warn("[DevStreamPopout] Access revoked, closing window");
            alert(
              "Developer Streams access has been revoked. This window will now close."
            );
            window.close();
          }
        } catch (e) {
          logger.warn(
            "[DevStreamPopout] Failed to parse settings from storage event",
            e
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [selectedOrgId, isFullAdmin, hasAccess]);

  useEffect(() => {
    setMounted(true);

    // Apply theme to body
    document.body.className = theme;

    // Clean up localStorage flag when window closes
    return () => {
      localStorage.removeItem("devStreamPopout");
    };
  }, [theme]);

  // Handle window close
  const handleClose = () => {
    window.close();
  };

  if (!mounted || accessChecking) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Initializing Developer Stream...
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to view Developer Streams.</p>
        <p>This window will close automatically.</p>
      </div>
    );
  }

  return (
    <div className="dev-stream-popout-container">
      {/* Session Expiration Warning */}
      <SessionExpirationWarning />

      <DevStreamPane
        events={devStreamData.events}
        filters={devStreamData.filters}
        updateFilter={devStreamData.updateFilter}
        ypEnabled={devStreamData.ypEnabled}
        mcEnabled={devStreamData.mcEnabled}
        toggleYP={devStreamData.toggleYP}
        toggleMC={devStreamData.toggleMC}
        clearEvents={devStreamData.clearEvents}
        sendTestEvent={devStreamData.sendTestEvent}
        connected={devStreamData.connected}
        authenticated={devStreamData.authenticated}
        connectionError={devStreamData.connectionError}
        switchOrganization={devStreamData.switchOrganization}
        selectedOrgId={devStreamData.selectedOrgId}
        maxEvents={devStreamData.maxEvents}
        updateMaxEvents={devStreamData.updateMaxEvents}
        onClose={handleClose}
        isPopout={true}
        isFullAdmin={isFullAdmin}
      />
    </div>
  );
}

export default DevStreamPopout;
